import { groupBy } from 'lodash-es';
import ColourMapping from '@/components/commons/ColourUtils.js';
import CountryUtils from '@/components/commons/CountryUtils.js';
import { getInitialState } from '@/components/commons/ServerSideRendererUtils.js';
import LocationService from '@/services/LocationService';

const initialState = getInitialState() || {};
const search = {
  app: process.env.ALGOLIA_APP_ID,
  key: process.env.ALGOLIA_API_KEY,
  productsPriceHighLowIndex: process.env.PRODUCTS_PRICE_HIGH_LOW_INDEX,
  productsPriceLowHighIndex: process.env.PRODUCTS_PRICE_LOW_HIGH_INDEX,
  productsNewestIndex: process.env.PRODUCTS_NEWEST_INDEX,
  productsPopularityIndex: process.env.PRODUCTS_POPULARITY_INDEX,
  productsSuggestionsIndex: process.env.PRODUCTS_SUGGESTIONS_INDEX,
  helpCenterIndex: process.env.ALGOLIA_HELP_CENTER_INDEX,
};

const algoliaClient = null;

async function getAlgoliaClient() {
  if (algoliaClient) {
    return algoliaClient;
  }
  const algoliasearch = (await import(/* webpackChunkName: "algoliasearch" */ 'algoliasearch')).default;
  return algoliasearch(search.app, search.key);
}

const Taxonomy = {
  attributes: initialState.taxonomyAttributes,
  attributeValues: initialState.taxonomyAttributeValues,
  categories: initialState.taxonomyCategories,
};

const MATERIAL_TITLE = 'Material';

const TAXONOMY_CATEGORY_FACET = 'taxonomy_Category';
const TAXONOMY_FACET_PREFIX = 'taxonomy_';
const SALE_FACET = 'sale';
const SALE_PROMOTION = 'PROMOTION';
const SALE_CLEARANCE = 'CLEARANCE';

const AlgoliaIndex = {
  PRICE_HIGH: search.productsPriceHighLowIndex,
  PRICE_LOW: search.productsPriceLowHighIndex,
  NEWEST: search.productsNewestIndex,
};

/**
 * Load product results from Megastore's API.
 *
 * @param {Object} filters
 * @param {Number} page
 * @param {Number} pageSize
 * @param {Object} additionalParams
 */
export async function loadProducts(filters, page, pageSize, additionalParams) {
  const index = AlgoliaIndex[filters.sort] || search.productsPopularityIndex;
  const queries = toAlgoliaQueries(index, filters, page, pageSize, additionalParams, Taxonomy);
  const queriesList = Object.keys(queries)
    .map((queryKey) => ({ key: queryKey, value: queries[queryKey] }))
    .filter((q) => q.value);

  const result = await (await getAlgoliaClient()).multipleQueries(queriesList.map((q) => q.value));

  const resultMapped = {
    productQuery: {
      hits: [],
    },
  };
  const algoliaResultItems = result.results || [];

  algoliaResultItems.forEach((r, i) => {
    resultMapped[queriesList[i].key] = r;
  });

  return processResults(resultMapped, filters, page);
}

export function processResults(resultMapped, filters, page) {
  const products = processProducts(resultMapped.productQuery, page, CountryUtils.getCountryByCurrencyId(filters.currency), Taxonomy);
  Object.keys(resultMapped).forEach((key) => {
    if (key === TAXONOMY_CATEGORY_FACET) {
      const categories = processAttributes(resultMapped[key].facets, Taxonomy);
      if (categories[0]) {
        products.categories = categories[0].attributeValues;
      } else {
        products.categories = [];
      }
    } else if (key === SALE_FACET) {
      const saleResult = resultMapped[key];
      const saleFacets = saleResult.facets[SALE_FACET];
      products.onSaleProductsCount = saleFacets[SALE_PROMOTION] || 0;
      products.onClearanceProductsCount = saleFacets[SALE_CLEARANCE] || 0;
    } else if (key.startsWith(TAXONOMY_FACET_PREFIX)) {
      const attributes = processAttributes(resultMapped[key].facets || {}, Taxonomy);
      attributes.forEach((attribute) => {
        products.attributes.forEach((a, i) => {
          if (a.id === attribute.id) {
            products.attributes[i] = attribute;
          }
        });
      });
    }
  });

  // Filter attributes that aren't valued for every product in the results
  products.attributes = products.attributes.filter(
    (attribute) =>
      attribute.title === MATERIAL_TITLE ||
      attribute.attributeValues.reduce((acc, current) => acc + current.productsCount, 0) >= products.productsCount,
  );

  // Check if no hits were returned for the original query
  if (resultMapped.productQuery && resultMapped.productQuery.queryAfterRemoval) {
    products.queryAfterRemoval = resultMapped.productQuery.queryAfterRemoval
      .split(/<em>|<\/em>/)
      .filter((term, index) => index % 2 === 0)
      .join('')
      .trim();
  } else {
    products.queryAfterRemoval = products.filters.query;
  }

  return products;
}

export async function loadSuggestions(query, currency, options) {
  const { results } = await (await getAlgoliaClient()).multipleQueries([
    {
      indexName: search.productsSuggestionsIndex,
      query: query || '',
      params: {
        analytics: options && options.analytics && !LocationService.isOffice() ? true : false,
        hitsPerPage: options && options.suggestionCount ? options.suggestionCount : 8,
        page: 0,
      },
    },
    {
      indexName: search.productsPopularityIndex,
      query: query || '',
      params: {
        analytics: options && options.analytics && !LocationService.isOffice() ? true : false,
        hitsPerPage: options && options.productCount ? options.productCount : 3,
        page: 0,
        clickAnalytics: true,
      },
    },
  ]);

  return {
    suggestions: results.length >= 1 ? results[0].hits : [],
    products: results.length > 1 ? processProducts(results[1], 1, CountryUtils.getCountryByCurrencyId(currency), Taxonomy).products : [],
    productsCount: results.length > 1 ? results[1].nbHits : 0,
    productsQueryId: results.length > 1 ? results[1].queryID : null,
    productsQueryIndex: results.length > 1 ? results[1].index : null,
  };
}

export function loadProductsWithParams(additionalParamsFn) {
  return function (filters, page, pageSize, params) {
    return loadProducts(filters, page, pageSize, { ...(additionalParamsFn() || {}), ...params });
  };
}

export function processProducts(result, page, country, taxonomy) {
  if (result.errors) {
    throw result.errors;
  }

  const ungroupedProducts = result.hits.map((hit) => ({
    id: hit.objectID,
    baseId: hit.parentID,
    skuNo: hit.sku,
    title: hit.title,
    url: hit.productPath ? `/${hit.productPath}` : hit.productUrl,
    productImage: {
      key: hit.imageUrl,
      url: hit.imageUrl,
      alt: hit.title,
    },
    swatchImage: {
      key: hit.swatchUrl,
      url: hit.swatchUrl,
      alt: hit.title,
    },
    price: country.currencySymbol + hit[`price_${country.currency.toUpperCase()}`],
    originalPrice: country.currencySymbol + hit[`originalPrice_${country.currency.toUpperCase()}`],
    isOnSale: hit.onSale,
    isInStock: hit.inventoryCount > 0,
    isOnClearance: hit.onClearance,
    status: 'AVAILABLE',
    sequence: hit._distinctSeqID,
    page,
  }));

  let previous = null;
  const products = [];
  ungroupedProducts.forEach((product) => {
    if (!product.baseId || !previous || !previous.baseId || (previous && previous.baseId !== product.baseId)) {
      product.variations = [];
      previous = product;
      products.push(product);
    } else {
      previous.variations.push(product);
    }
  });

  let attributes = processAttributes(result.facets || {}, taxonomy);
  const inStockCount = result.facets && result.facets.inStock ? result.facets.inStock.true : 0;
  const onSaleCount = result.facets && result.facets.onSale ? result.facets.onSale.true : 0;
  const onClearanceCount = result.facets && result.facets.onClearance ? result.facets.onClearance.true : 0;
  let categories = attributes.filter((a) => a.id === 'Category');
  attributes = attributes.filter((a) => a.id !== 'Category');
  if (categories.length) {
    categories = categories[0].attributeValues;
    categories.forEach((c) => (c.enabled = true));
  } else {
    categories = null;
  }

  const formattedResult = {
    products,
    attributes,
    categories,
    filters: {
      currency: country ? country.currencyId : null,
    },
    productsCount: result.nbHits,
    onSaleProductsCount: onSaleCount,
    onClearanceProductsCount: onClearanceCount,
    inStockProductsCount: inStockCount,
    queryId: result.queryID,
    queryIndex: result.index,
  };

  return formattedResult;
}

export function processAttributes(facets, taxonomy) {
  return Object.keys(facets)
    .filter((f) => f.startsWith(TAXONOMY_FACET_PREFIX))
    .map((facetText) => {
      const facet = facets[facetText];
      const facetName = facetText.replace(TAXONOMY_FACET_PREFIX, '');
      const facetParts = facetName.split('_');
      const attributeId = facetParts.length > 1 ? facetParts[1] : facetParts[0];
      const attributeName = facetParts[0];

      return {
        id: attributeId,
        title: attributeName,
        name: attributeName,
        attributeValues: Object.keys(facet)
          .map((valueName) => {
            const value = facet[valueName];
            const valueNameParts = valueName.split('_');
            const attributeValueId = valueNameParts.length > 1 ? valueNameParts[1] : valueNameParts[0];
            const isCategoryFacet = facetName === 'Category';

            if ((isCategoryFacet && taxonomy.categories[attributeValueId]) || (!isCategoryFacet && taxonomy.attributeValues[attributeValueId])) {
              return {
                id: attributeValueId,
                name: isCategoryFacet ? taxonomy.categories[attributeValueId] : taxonomy.attributeValues[attributeValueId].value,
                userFriendlyName: isCategoryFacet ? taxonomy.categories[attributeValueId] : taxonomy.attributeValues[attributeValueId].value,
                productsCount: value,
                hex: facetName === 'Color_1' ? ColourMapping[valueNameParts[0]] : null,
                enabled: true,
              };
            }
            return null;
          })
          .filter((av) => av),
      };
    });
}

export function toAlgoliaQueries(index, filters, page, pageSize, additionalParams, taxonomy) {
  const { query } = filters;

  const queryStrings = {
    stockQuery: filters.inStock ? 'inStock:true' : undefined,
    saleQueries: toAlgoliaSaleFilters(filters, taxonomy), // filters.onSale ? 'onSale:true' : undefined,
    // clearanceQuery: filters.onClearance ? 'onClearance:true' : undefined,
    facetQueries: toAlgoliaFacetFilters(filters, taxonomy),
    categoryQueries: toAlgoliaCategoryFilters(filters, taxonomy),
  };

  const queries = {
    productQuery: toAlgoliaQuery(index, query, page, pageSize, queryStrings),
  };

  Object.keys(queryStrings)
    .filter((key) => queryStrings[key])
    .forEach((key) => {
      let copiedQueryStrings = { ...queryStrings };

      if (key === 'facetQueries') {
        queryStrings[key].forEach((val, i) => {
          copiedQueryStrings = { ...queryStrings };
          const queryStringsArray = [...queryStrings[key]];
          const taxonomyString = queryStringsArray[i][0].split(':')[0];
          queryStringsArray.splice(i, 1);
          copiedQueryStrings[key] = queryStringsArray;
          queries[taxonomyString] = toAlgoliaQuery(index, query, 1, 0, copiedQueryStrings, [taxonomyString]);
        });
      } else if (key === 'categoryQueries') {
        delete copiedQueryStrings[key];
        queries[TAXONOMY_CATEGORY_FACET] = toAlgoliaQuery(index, query, 1, 0, copiedQueryStrings, [TAXONOMY_CATEGORY_FACET]);
      } else if (key === 'saleQueries') {
        delete copiedQueryStrings[key];
        queries[SALE_FACET] = toAlgoliaQuery(index, query, 1, 0, copiedQueryStrings, [SALE_FACET]);
      } else {
        delete copiedQueryStrings[key];
        queries[key] = toAlgoliaQuery(index, query, 1, 0, copiedQueryStrings, [queryStrings[key].split(':')[0]]);
      }
    });

  return queries;
}

export function toAlgoliaQuery(index, queryString, page, pageSize, filterParams, facets) {
  const query = {
    indexName: index,
    query: queryString || '',
    params: {
      distinct: 12,
      hitsPerPage: pageSize,
      page: page - 1,
      facets: facets || ['*'],
      removeWordsIfNoResults: 'lastWords',
      analytics: !LocationService.isOffice(),
      clickAnalytics: true,
    },
  };

  if (filterParams.stockQuery) {
    query.params.filters = filterParams.stockQuery;
  }

  const facetFilters = [...(filterParams.facetQueries || []), filterParams.categoryQueries, filterParams.saleQueries].filter((q) => q);
  if(facetFilters && facetFilters.length) {
    query.params.facetFilters = facetFilters;
  }

  return query;
}

export function toAlgoliaFacetFilters(filters, taxonomy) {
  if (filters.attributeValues && filters.attributeValues.length) {
    const filterAttributeValues = filters.attributeValues.map((attributeValueId) => {
      const attributeValue = taxonomy.attributeValues[attributeValueId];
      const attributeId = attributeValue.taxonomyAttributeId;
      return `${TAXONOMY_FACET_PREFIX}${taxonomy.attributes[attributeId]}_${attributeId}:${attributeValue.value}_${attributeValue.id}`;
    });

    return Object.values(groupBy(filterAttributeValues, (attributeValue) => attributeValue.split(':')[0]));
  }
  return undefined;
}

export function toAlgoliaCategoryFilters(filters, taxonomy) {
  if (filters.attributesProductsValues && filters.attributesProductsValues.length) {
    return filters.attributesProductsValues.map((categoryId) => `${TAXONOMY_CATEGORY_FACET}:${taxonomy.categories[categoryId]}_${categoryId}`);
  }
  return undefined;
}

export function toAlgoliaSaleFilters(filters, taxonomy) {
  const result = [];

  if (filters.onSale) {
    result.push(`${SALE_FACET}:${SALE_PROMOTION}`);
  }

  if (filters.onClearance) {
    result.push(`${SALE_FACET}:${SALE_CLEARANCE}`);
  }

  return result.length ? result : undefined;
}

export async function loadHelpCenterResults(query) {
  const { results } = await (await getAlgoliaClient()).search([
    {
      indexName: search.helpCenterIndex,
      query: query || '',
      params: {
          getRankingInfo: true
      }
    }
  ]);

  return { results: results[0] };
}

